import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Icon, ProgressBar } from '../../ui';
import { Categoria } from '../../servicos/categorias/typeCategoria';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { valorParaTexto } from '../../utils/Utils';
import { dataCurta, hexToRgba } from './uteis';
import styles from './CategoriasCard.module.css';

const paraISO = (data: Date): string => {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
};

type CategoriasCardProps = {
  transacoes: Transacao[];
  categorias: Categoria[];
  /** Período em exibição — vai junto no drill-down pras Movimentações. */
  inicio: Date;
  fim: Date;
};

type LinhaCategoria = {
  chave: string;
  nome: string;
  icone: string;
  cor: string; // hex da categoria; vazio = sem cor definida
  valor: number;
  transacoes: Transacao[];
  agregada: boolean; // linha "Outras" (não expande)
};

/**
 * "Pra onde foi o dinheiro": top 6 categorias de despesa do período como barras
 * horizontais; clicar numa categoria expande as maiores transações dela inline.
 */
export default function CategoriasCard({ transacoes, categorias, inicio, fim }: CategoriasCardProps) {
  const [expandida, setExpandida] = useState<string | null>(null);
  const navigate = useNavigate();

  const { linhas, total } = useMemo(() => {
    const despesas = transacoes.filter((transacao) => transacao.tipo === 'D');

    const grupos: { [chave: string]: Transacao[] } = {};
    for (const transacao of despesas) {
      const chave = transacao.categoriaId != null ? String(transacao.categoriaId) : 'sem';
      (grupos[chave] = grupos[chave] ?? []).push(transacao);
    }

    const todas: LinhaCategoria[] = Object.keys(grupos)
      .map((chave) => {
        const categoria = chave === 'sem' ? undefined : categorias.find((c) => c.id === Number(chave));
        const lista = grupos[chave];
        return {
          chave,
          nome: categoria?.nome ?? 'Sem categoria',
          icone: categoria?.icone ?? 'category',
          cor: categoria?.corIdentificacao?.trim() ?? '',
          valor: lista.reduce((soma, transacao) => soma + Number(transacao.valor), 0),
          transacoes: lista,
          agregada: false
        };
      })
      .sort((a, b) => b.valor - a.valor);

    const principais = todas.slice(0, 6);
    const resto = todas.slice(6);
    if (resto.length > 0) {
      principais.push({
        chave: 'outras',
        nome: `Outras (${resto.length})`,
        icone: 'more_horiz',
        cor: '',
        valor: resto.reduce((soma, linha) => soma + linha.valor, 0),
        transacoes: [],
        agregada: true
      });
    }

    return {
      linhas: principais,
      total: todas.reduce((soma, linha) => soma + linha.valor, 0)
    };
  }, [transacoes, categorias]);

  const maiorValor = linhas.length > 0 ? linhas[0].valor : 0;

  return (
    <Card className={styles.cartao}>
      <div className={styles.cabecalho}>
        <h2 className={styles.titulo}>Pra onde foi o dinheiro</h2>
        {total > 0 && <span className={styles.total}>{valorParaTexto(total, true)}</span>}
      </div>

      {linhas.length === 0 ? (
        <div className={styles.vazio}>Nenhuma despesa registrada neste período.</div>
      ) : (
        <div className={styles.lista}>
          {linhas.map((linha) => {
            const percentualDoTotal = total > 0 ? Math.round((linha.valor / total) * 100) : 0;
            const aberta = expandida === linha.chave;
            const corIcone = linha.cor || 'var(--textSecondary)';
            const fundoIcone = linha.cor ? hexToRgba(linha.cor, 0.12) : 'var(--medium-gray)';
            const maioresTransacoes = [...linha.transacoes]
              .sort((a, b) => Number(b.valor) - Number(a.valor))
              .slice(0, 5);

            return (
              <div key={linha.chave} className={styles.item}>
                <button
                  type="button"
                  className={styles.linha}
                  onClick={() => !linha.agregada && setExpandida(aberta ? null : linha.chave)}
                  disabled={linha.agregada}
                >
                  <span className={styles.iconeCategoria} style={{ color: corIcone, background: fundoIcone }}>
                    <Icon nome={linha.icone} tamanho={18} />
                  </span>
                  <span className={styles.nome}>{linha.nome}</span>
                  <span className={styles.percentual}>{percentualDoTotal}%</span>
                  <span className={styles.valor}>{valorParaTexto(linha.valor, true)}</span>
                </button>
                <ProgressBar
                  percentual={maiorValor > 0 ? (linha.valor / maiorValor) * 100 : 0}
                  cor={linha.cor || 'var(--placeholder)'}
                />
                {aberta && (
                  <div className={styles.detalhes}>
                    {maioresTransacoes.map((transacao) => (
                      <div key={transacao.id} className={styles.transacao}>
                        <span className={styles.transacaoDescricao}>{transacao.descricao || linha.nome}</span>
                        <span className={styles.transacaoData}>{dataCurta(new Date(transacao.data))}</span>
                        <span className={styles.transacaoValor}>{valorParaTexto(Number(transacao.valor), true)}</span>
                      </div>
                    ))}
                    {linha.transacoes.length > 5 && (
                      <span className={styles.maisTransacoes}>
                        + {linha.transacoes.length - 5} outras nesta categoria
                      </span>
                    )}
                    {linha.chave !== 'sem' && (
                      <button
                        type="button"
                        className={styles.verTudo}
                        onClick={() =>
                          navigate('/movimentacoes', {
                            state: {
                              categoriaIds: [linha.chave],
                              dataInicial: paraISO(inicio),
                              dataFinal: paraISO(fim)
                            }
                          })
                        }
                      >
                        Ver nas movimentações
                        <Icon nome="arrow_forward" tamanho={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
